import {
  BulkModal,
  BulkSelectAction,
  ZapBulkActionOptions,
  ZapModal,
} from "@zapier/shared";
import { ZAP_ASSET_ID_PREFIX } from "@zapier/utils";

export type ActionContext = "folder" | "trash";

export interface BulkActionEntry<TKey extends string = string> {
  icon: string;
  textKey: TKey;
  order: number;
  contexts: ActionContext[];
  isEnabled: boolean;
  isVisible?: (ids: string[]) => boolean;
  onClick: (ids: string[]) => void;
}

export const isAllZaps = (ids: string[]) =>
  ids.length > 0 && ids.every((id) => id.startsWith(ZAP_ASSET_ID_PREFIX));

export function isAllOfPrefixes(ids: string[], prefixes: string[]) {
  return (
    ids.length > 0 &&
    ids.every((id) => prefixes.some((prefix) => id.startsWith(prefix)))
  );
}

export function isAllZapsOrPrefixes(ids: string[], prefixes: string[]) {
  return (
    ids.length > 0 &&
    ids.every(
      (id) =>
        id.startsWith(ZAP_ASSET_ID_PREFIX) ||
        prefixes.some((prefix) => id.startsWith(prefix)),
    )
  );
}

export interface RegistryParameters {
  canBulkChangeOwner: boolean;
  trashEnabledPrefixes: string[];
  isDeleteAllowed: boolean;
  isTrashFolder: boolean;
  setSelectedAssetIds: (ids: string[]) => void;
  setCurrentZapModal: (modal: ZapModal) => void;
  setCurrentBulkModal: (modal: BulkModal) => void;
  onMoveClicked?: (ids: string[]) => void;
  onDeleteClicked?: (ids: string[]) => void;
  onCopyToAnotherAccountClicked: (ids: string[]) => void;
  isCopyToAnotherAccountVisible: (ids: string[]) => boolean;
  handleBulkRestore: (ids: string[]) => void;
  emit: (event: { optionSelected: ZapBulkActionOptions }) => void;
}

export function createBulkActionRegistry({
  canBulkChangeOwner,
  trashEnabledPrefixes,
  isDeleteAllowed,
  isTrashFolder,
  setSelectedAssetIds,
  setCurrentZapModal,
  setCurrentBulkModal,
  onMoveClicked,
  onDeleteClicked,
  onCopyToAnotherAccountClicked,
  isCopyToAnotherAccountVisible,
  handleBulkRestore,
  emit,
}: RegistryParameters): BulkActionEntry[] {
  const hasTrashEnabled = trashEnabledPrefixes.length > 0;

  const isAllTrashEnabled = (ids: string[]) =>
    isAllOfPrefixes(ids, trashEnabledPrefixes);

  const deleteVisibility = hasTrashEnabled
    ? isTrashFolder
      ? isAllTrashEnabled
      : (ids: string[]) => isAllZapsOrPrefixes(ids, trashEnabledPrefixes)
    : isAllZaps;

  // Zap delete has always been a soft delete into the same 30-day trash.
  const landsInTrash = !isTrashFolder;

  const deleteTextKey = landsInTrash
    ? "move-to-trash"
    : hasTrashEnabled
      ? "permanently-delete"
      : "delete";

  return [
    {
      icon: "personSettings",
      textKey: "change-owner",
      order: 0,
      contexts: ["folder"] as ActionContext[],
      isEnabled: canBulkChangeOwner,
      isVisible: isAllZaps,
      onClick: (ids: string[]) => {
        setSelectedAssetIds(ids);
        setCurrentZapModal(ZapModal.ChangeOwner);
        emit({ optionSelected: ZapBulkActionOptions.ChangeOwner });
      },
    },
    {
      icon: "actionDownload",
      textKey: "export-zaps",
      order: 1,
      contexts: ["folder"] as ActionContext[],
      isEnabled: true,
      isVisible: isAllZaps,
      onClick: (ids: string[]) => {
        setSelectedAssetIds(ids);
        setCurrentZapModal(ZapModal.Export);
        emit({ optionSelected: ZapBulkActionOptions.Export });
      },
    },
    {
      icon: "actionMoveToFolder",
      textKey: "move-to-folder",
      order: 2,
      contexts: ["folder"] as ActionContext[],
      isEnabled: true,
      onClick: (ids: string[]) => {
        setSelectedAssetIds(ids);
        if (isAllZaps(ids)) {
          setCurrentZapModal(ZapModal.Move);
        } else {
          onMoveClicked?.(ids);
          setCurrentBulkModal(BulkModal.Move);
        }
        emit({ optionSelected: ZapBulkActionOptions.Move });
      },
    },
    {
      icon: "arrowExit",
      textKey: "copy-to-another-account",
      order: 3,
      contexts: ["folder"] as ActionContext[],
      isEnabled: true,
      isVisible: isCopyToAnotherAccountVisible,
      onClick: (ids: string[]) => {
        setSelectedAssetIds(ids);
        onCopyToAnotherAccountClicked(ids);
      },
    },
    {
      icon: "arrowRefresh",
      textKey: "restore",
      order: 4,
      contexts: ["trash"] as ActionContext[],
      isEnabled: hasTrashEnabled,
      // No zap exclusion needed: zap ids only reach here once the flag has
      // put the zap prefix in trashEnabledPrefixes.
      isVisible: isAllTrashEnabled,
      onClick: (ids: string[]) => {
        setSelectedAssetIds(ids);
        handleBulkRestore(ids);
      },
    },
    {
      icon: "actionTrash",
      textKey: deleteTextKey,
      order: 5,
      contexts: ["folder", "trash"] as ActionContext[],
      isEnabled: isDeleteAllowed,
      isVisible: deleteVisibility,
      onClick: (ids: string[]) => {
        setSelectedAssetIds(ids);
        const useBulkDeleteModal =
          hasTrashEnabled && (isTrashFolder || !isAllZaps(ids));
        if (useBulkDeleteModal) {
          onDeleteClicked?.(ids);
          setCurrentBulkModal(BulkModal.Delete);
        } else {
          setCurrentZapModal(ZapModal.Delete);
        }
        emit({ optionSelected: ZapBulkActionOptions.Delete });
      },
    },
  ];
}

export function resolveActions<TKey extends string>(
  registry: BulkActionEntry<TKey>[],
  currentContext: ActionContext,
  translate: (key: TKey) => string,
): BulkSelectAction[] {
  return [...registry]
    .filter(
      (entry) => entry.isEnabled && entry.contexts.includes(currentContext),
    )
    .sort((a, b) => a.order - b.order)
    .map(({ textKey, onClick, isVisible, icon }) => ({
      onClick,
      isVisible,
      text: translate(textKey),
      icon,
    }));
}
